// Pestaña «Promociones» del cajón: una tarjeta por promo vigente con beneficio, vigencia, a quién
// aplica y alcance. Si la fuente no respondió se dice «sin fuente»; nunca un vacío fingido.
import { BadgePercent } from 'lucide-react';
import { EmptyState } from '@/kit';
import { DetailCard, DetailKeyValue, GapMark } from '@/shared/patterns/DetailCard';
import { NoSourceBlock } from '@/shared/patterns/NoSource';
import { StatusPill } from '@/shared/patterns/Pills';
import {
    benefitText,
    PROMOTION_KIND_LABEL,
    sortPromotions,
    type PromotionField,
    type PromotionView,
    type PromotionsTabState,
} from './promotionView';

export const NO_PROMOTIONS_TITLE = 'Este producto no tiene promociones vigentes';

function FieldRow({ label, field }: { label: string; field: PromotionField }) {
    if (field.text === undefined) {
        return <DetailKeyValue label={label} note={field.missing ?? 'La fuente no lo trae.'} />;
    }
    return (
        <DetailKeyValue
            label={label}
            value={field.text}
            plain
            mark={field.missing === undefined ? undefined : <GapMark note={field.missing} />}
        />
    );
}

function PromotionCard({ promotion }: { promotion: PromotionView }) {
    return (
        <DetailCard
            title={promotion.name}
            meta={
                promotion.kind === undefined ? undefined : (
                    <StatusPill tone="neutral">{PROMOTION_KIND_LABEL[promotion.kind]}</StatusPill>
                )
            }
        >
            <DetailKeyValue label="Beneficio" value={benefitText(promotion)} />
            <FieldRow label="Vigencia" field={promotion.validity} />
            <FieldRow label="Aplica a" field={promotion.audience} />
            {promotion.scope === undefined ? null : (
                <FieldRow label="Alcance" field={promotion.scope} />
            )}
        </DetailCard>
    );
}

export function PromotionsTab({ state }: { state: PromotionsTabState }) {
    if (state.kind === 'loading') {
        return <NoSourceBlock>Cargando las promociones…</NoSourceBlock>;
    }
    if (state.kind === 'noSource') {
        return (
            <NoSourceBlock>
                <span className="font-semibold not-italic">sin fuente</span> · {state.reason}
            </NoSourceBlock>
        );
    }
    const note =
        state.unavailableNote === undefined ? null : (
            <NoSourceBlock>
                <span className="font-semibold not-italic">sin fuente</span> ·{' '}
                {state.unavailableNote}
            </NoSourceBlock>
        );
    if (state.promotions.length === 0) {
        // Con una fuente caída el vacío no es de verdad: solo se dice qué falta.
        if (note !== null) return note;
        return (
            <EmptyState
                icon={<BadgePercent className="size-6" aria-hidden />}
                title={NO_PROMOTIONS_TITLE}
                className="py-8"
            />
        );
    }
    return (
        <section aria-label="Promociones vigentes">
            {note}
            {sortPromotions(state.promotions).map((promotion) => (
                <PromotionCard
                    key={`${promotion.kind ?? ''}:${promotion.id}`}
                    promotion={promotion}
                />
            ))}
        </section>
    );
}
